import { useCallback, useEffect, useRef, useState } from 'react'
import { getEcommerceMethods, getErpMethods, getMethodMappings, saveMethodMappings } from './methodMappingApi'

export const createRow = (rowId, mapping) => ({
    rowId,
    erpMethod: (mapping && mapping.erpMethod) || '',
    ecommerceMethod: (mapping && mapping.ecommerceMethod) || '',
    isEcommerceDefault: Boolean(mapping && mapping.isEcommerceDefault),
    isErpDefault: Boolean(mapping && mapping.isErpDefault),
    isNew: Boolean(mapping && mapping.isNew)
})

export const isRowComplete = (row) => row.erpMethod !== '' && row.ecommerceMethod !== ''

/**
 * Owns the state for a method mapping tab (payment or shipping): both method lists,
 * the editable rows and the load/save lifecycle. `resource` selects the endpoint
 * family, `entityLabel` only shapes the user-facing messages.
 */
const useMethodMapping = (resource, entityLabel, connectorId, instancesId) => {
    const [erpMethods, setErpMethods] = useState([])
    const [ecommerceMethods, setEcommerceMethods] = useState([])
    const [rows, setRows] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [saveStatus, setSaveStatus] = useState(null)

    const nextRowId = useRef(0)
    const isMounted = useRef(true)
    const loadedKey = useRef(null)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (!connectorId || !instancesId) {
            setIsLoading(false)
            return
        }

        // React 18 StrictMode runs effects twice in development. This ref survives
        // that simulated remount, so each connector/instance is fetched exactly
        // once - no duplicate or cancelled requests in the network tab.
        const key = `${resource}|${connectorId}|${instancesId}`
        if (loadedKey.current === key) {
            return
        }
        loadedKey.current = key

        setIsLoading(true)
        setLoadError('')

        Promise.all([
            getErpMethods(resource, connectorId, instancesId),
            getEcommerceMethods(resource, connectorId, instancesId),
            getMethodMappings(resource, connectorId, instancesId)
        ])
            .then(([erpOptions, ecommerceOptions, mappings]) => {
                if (!isMounted.current) {
                    return
                }
                setErpMethods(erpOptions)
                setEcommerceMethods(ecommerceOptions)
                setRows(mappings.map((mapping) => {
                    nextRowId.current += 1
                    return createRow(nextRowId.current, mapping)
                }))
                setIsLoading(false)
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setLoadError(`Unable to load the ${entityLabel} details. Please try again.`)
                setIsLoading(false)
                // eslint-disable-next-line no-console
                console.error(`${resource} mapping load failed`, error)
            })
    }, [resource, entityLabel, connectorId, instancesId])

    const addRow = useCallback((mapping) => {
        setSaveStatus(null)
        setRows((current) => {
            nextRowId.current += 1
            return [createRow(nextRowId.current, { ...mapping, isNew: true }), ...current]
        })
    }, [])

    const updateRow = useCallback((rowId, changes) => {
        setSaveStatus(null)
        setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)))
    }, [])

    const removeRow = useCallback((rowId) => {
        setSaveStatus(null)
        setRows((current) => current.filter((row) => row.rowId !== rowId))
    }, [])

    const submitMappings = useCallback(() => {
        const payload = rows.filter(isRowComplete)

        setIsSaving(true)
        setSaveStatus(null)

        return saveMethodMappings(resource, connectorId, instancesId, payload)
            .then(() => {
                if (!isMounted.current) {
                    return
                }
                setRows((current) => current.map((row) => ({ ...row, isNew: false })))
                setSaveStatus({
                    result: true,
                    message: `${payload.length} ${entityLabel} mapping${payload.length === 1 ? '' : 's'} saved successfully.`
                })
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setSaveStatus({ result: false, message: `Unable to save the ${entityLabel} mappings.` })
                // eslint-disable-next-line no-console
                console.error(`${resource} mapping save failed`, error)
            })
            .finally(() => {
                if (isMounted.current) {
                    setIsSaving(false)
                }
            })
    }, [rows, resource, entityLabel, connectorId, instancesId])

    const dismissStatus = useCallback(() => setSaveStatus(null), [])

    return {
        erpMethods,
        ecommerceMethods,
        rows,
        isLoading,
        loadError,
        isSaving,
        saveStatus,
        addRow,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    }
}

export default useMethodMapping
